
import { useState, useMemo } from 'react';
import { ImageLibraryItem } from '@/hooks/runware/image-library/types';

interface UseImageCardProps {
  image: ImageLibraryItem;
  onCardClick?: (image: ImageLibraryItem) => void;
  onFavorite?: (imageId: string) => void;
  favoriteCount?: number;
  isFavorited?: boolean;
}

export const useImageCard = (
  image: ImageLibraryItem,
  onDelete?: (id: string) => void,
  onSelect?: (image: ImageLibraryItem) => void,
  onImageLoaded?: (id: string) => void
) => {
  const [isLoading, setIsLoading] = useState(false);
  const [imageLoaded, setImageLoaded] = useState(false);

  const handleCardClick = () => {
    if (onSelect && !isLoading && image) {
      onSelect(image);
    }
  };

  const handleFavoriteClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (onDelete && image?.id) {
      onDelete(image.id);
    }
  };

  const handleImageLoad = () => {
    setImageLoaded(true);
  };

  const handleImageError = () => {
    setImageLoaded(false);
  };

  // Add the missing handleSelect method that ImageCard expects
  const handleSelect = () => {
    if (onSelect && !isLoading && image) {
      onSelect(image);
    }
  };

  // Add the missing cacheLoadedImage method that ImageCard expects
  const cacheLoadedImage = (imageUrl: string, imgElement: HTMLImageElement) => {
    // Simple caching implementation - browser handles most of this automatically
    // Could be enhanced with more sophisticated caching logic if needed
    console.log('Image cached:', imageUrl);
  };

  // Memoize image data to prevent unnecessary recalculations
  const imageData = useMemo(() => {
    if (!image) return { url: '', prompt: '', width: 0, height: 0, id: '' };
    return {
      url: image.image_url || '',
      prompt: image.original_prompt || '',
      width: image.width || 0,
      height: image.height || 0,
      id: image.id || ''
    };
  }, [image?.image_url, image?.original_prompt, image?.width, image?.height, image?.id]);

  const cardState = useMemo(() => ({
    isLoading,
    imageLoaded,
    isFavorited: image?.is_favorite || false,
    favoriteCount: image?.favorite_count || 0,
    imageUrl: imageData.url,
    prompt: imageData.prompt || 'No prompt available'
  }), [isLoading, imageLoaded, image?.is_favorite, image?.favorite_count, imageData.url, imageData.prompt]);

  return {
    cardState,
    handlers: {
      handleCardClick,
      handleFavoriteClick,
      handleImageLoad,
      handleImageError,
      handleSelect,
    },
    setIsLoading,
    handleImageLoad,
    cacheLoadedImage,
    handleSelect
  };
};
